import type { StoreApi } from 'zustand';
import type { IconMetadata, SpriteIndex } from '@/scripts/types';
import { calculateSpriteLayout } from '@/utils/sprite-loader';

export interface IconMetadataState {
  icons: IconMetadata[];
  isLoaded: boolean;
  spriteIndex: SpriteIndex | null;
  spriteUrl: string;
  spriteLayout: { width: number; height: number } | null;
  loadIcons: () => Promise<void>;
  searchIcons: (query: string) => IconMetadata[];
  getIconById: (id: string) => IconMetadata | undefined;
  getIconsByCategory: (query?: string) => Map<string, IconMetadata[]>;
  clearIconMetadata: () => void;
}

export const createIconMetadataSlice = (
  set: StoreApi<any>['setState'],
  get: StoreApi<any>['getState']
) => ({
  icons: [],
  isLoaded: false,
  spriteIndex: null,
  spriteUrl: '',
  spriteLayout: null,

  loadIcons: async () => {
    try {
      const [metadataResponse, spriteIndexResponse] = await Promise.all([
        fetch('/sprites/v1/icons-metadata.json'),
        (async () => {
          const pixelRatio = window.devicePixelRatio || 1;
          const suffix = pixelRatio >= 2 ? '@2x' : '';
          const spriteUrl = `/sprites/v1/icons${suffix}.png`;

          const response = await fetch(`/sprites/v1/icons${suffix}.json`);
          if (!response.ok) {
            throw new Error(`Failed to load sprite index: ${response.status}`);
          }

          const spriteIndex = await response.json();
          return { spriteIndex, spriteUrl };
        })(),
      ]);

      if (!metadataResponse.ok) {
        throw new Error(`Failed to load icon metadata: ${metadataResponse.status}`);
      }

      const metadata = await metadataResponse.json();
      const icons = metadata.icons || [];
      const spriteIndex = spriteIndexResponse.spriteIndex;
      const spriteLayout = calculateSpriteLayout(spriteIndex);

      set({
        icons,
        spriteIndex,
        spriteUrl: spriteIndexResponse.spriteUrl,
        spriteLayout,
        isLoaded: true,
      });
    } catch (error) {
      console.error('[IconMetadata] Error loading icon data:', error);
      set({ icons: [], spriteIndex: null, spriteUrl: '', isLoaded: false });
    }
  },

  searchIcons: (query: string) => {
    const { icons } = get();

    if (!query || query.trim() === '') {
      return icons;
    }

    const lowerQuery = query.toLowerCase().trim();
    return icons.filter((icon: IconMetadata) => {
      const nameMatch = icon.name.toLowerCase().includes(lowerQuery);
      const tagMatch = icon.tags?.some(tag => tag.toLowerCase().includes(lowerQuery));
      const categoryMatch = icon.category.toLowerCase().includes(lowerQuery);

      return nameMatch || tagMatch || categoryMatch;
    });
  },

  getIconById: (id: string) => {
    const { icons } = get();
    return icons.find((icon: IconMetadata) => icon.id === id);
  },

  getIconsByCategory: (query?: string) => {
    const { icons, searchIcons } = get();

    const filteredIcons = query ? searchIcons(query) : icons;

    const grouped = new Map<string, IconMetadata[]>();

    filteredIcons.forEach((icon: IconMetadata) => {
      const category = icon.category;
      if (!grouped.has(category)) {
        grouped.set(category, []);
      }
      grouped.get(category)!.push(icon);
    });

    const CATEGORY_ORDER = [
      'emergency',
      'industry',
      'utilities',
      'amenities',
      'infrastructure',
      'transport',
      'nature',
      'shapes',
      'weather',
    ];

    return new Map(
      [...grouped.entries()].sort(([a], [b]) => {
        const aIndex = CATEGORY_ORDER.indexOf(a.toLowerCase());
        const bIndex = CATEGORY_ORDER.indexOf(b.toLowerCase());
        if (aIndex !== -1 && bIndex !== -1) {
          return aIndex - bIndex;
        }
        if (aIndex !== -1) return -1;
        if (bIndex !== -1) return 1;
        return 0;
      })
    );
  },

  clearIconMetadata: () => {
    set({ icons: [], spriteIndex: null, spriteUrl: '', spriteLayout: null, isLoaded: false });
  },
});
